import VueRouter, { Route } from 'vue-router';
import { Dictionary, Store } from 'vuex';

import { initialRouteState, RouteNames } from '@/routing/config';

import { CollectionType } from '@/enums/collection-type';
import { TitleOrder } from '@/enums/title-order';
import { WebLocale } from '@/enums/web-locale';

import { SCORINGS } from '@/helpers/scoring-helper';

import { TitleObjectType } from '@/interfaces/titles';
import { getDefaultFilterCollection } from '@/stores/filter.store';

import type { Package } from '@/@types/graphql-types';
import type Genre from '@/interfaces/genre';
import type Provider from '@/interfaces/provider';
import type {
	CollectionStateUrlParams,
	CollectionStateUrlQuery,
	FilterCollection,
	FilterName,
} from '@/stores/filter.store';
import type { ExperimentFromUrl } from '@/stores/user.store';

const { isNavigationFailure, NavigationFailureType } = VueRouter;

const ratingMappings = {
	'imdb:score': 'rating_imdb',
	justwatch: 'rating_justwatch',
};

const ignoreFiltersOnUrl: (FilterName | string)[] = [
	'min_runtime',
	'max_runtime',
	'production_countries',
	'exclude_production_countries',
];

const StateHelper = {
	getRouteName,

	updateState(
		router: VueRouter,
		webLocale: WebLocale,
		predefinedRoute: string | undefined = undefined,
		collectionType: CollectionType,
		filters: Partial<FilterCollection> = {},
		providersByShortName: Record<string, Provider | Package>,
		genresByShortName: Record<string, Genre>,
		routeQuery: Route['query'] = {}
	) {
		let routeName = predefinedRoute ? predefinedRoute : getRouteName(webLocale, collectionType, filters);
		if (routeName === 'app.titles.popular.provider-plans' && (filters?.providers?.length || 0) > 1) {
			routeName = 'app.titles.popular.list';
		}

		const { params, query } = this.getUrlParamsFromFilterState(
			webLocale,
			routeName,
			filters,
			providersByShortName,
			genresByShortName,
			collectionType,
			routeQuery
		);

		// Note(igor): To avoid NavigationDuplicated exception, catch it and ignore
		// Exception comes in cases the same URL is used and only query is updated
		router.push({ name: routeName, params, query }).catch((error: any) => {
			if (
				!isNavigationFailure(error, NavigationFailureType.duplicated) &&
				!isNavigationFailure(error, NavigationFailureType.cancelled)
				// Note(valerio.mazza) Search.vue component generates a navigation canceled due to
				// a reset of the filters which sometimes triggers a navigation
			) {
				throw error;
			}
		});
	},

	calculateUrl(
		router: VueRouter,
		webLocale: WebLocale,
		predefinedRoute: string | undefined = undefined,
		collectionType: CollectionType,
		filters: Partial<FilterCollection> = {},
		providersByShortName: Record<string, Provider | Package>,
		genresByShortName: Record<string, Genre>
	) {
		const routeName = predefinedRoute ? predefinedRoute : getRouteName(webLocale, collectionType, filters);
		const { params, query } = this.getUrlParamsFromFilterState(
			webLocale,
			routeName,
			filters,
			providersByShortName,
			genresByShortName,
			collectionType,
			router.currentRoute.query
		);

		return router.resolve({ name: routeName, params, query }).href;
	},

	async applyExperimentFromRoute(route: Route, store: Store<any>): Promise<undefined> {
		const experimentFromUrl = this.getExperimentFromUrl(route.query);
		if (experimentFromUrl == null) return;

		await store.dispatch('user/applyExperimentFromUrl', experimentFromUrl);

		const search = new URLSearchParams(window.location.search);
		['remove_preferred_experiment', 'preferred_variant', 'snowplow'].forEach(param => search.delete(param));

		// Wait for Localforage to save latest user state
		await new Promise(resolve => setTimeout(resolve, 500));

		window.location.search = search.toString();
	},

	async applyRoute(route: Route, store: Store<any>, deferred?: Promise<any>) {
		if (deferred) await deferred;

		const searchSuggesterState = this.getSearchSuggesterStateFromUrl(route.query as CollectionStateUrlQuery);
		store.commit('searchSuggester/SET_PERSON_ID', searchSuggesterState.person_id || null);
		store.commit('searchSuggester/SET_QUERY', searchSuggesterState.q || '');

		const filterState = this.getFilterStateFromUrl(
			(route.name as RouteNames) ?? undefined,
			route.params,
			route.query as CollectionStateUrlQuery,
			store.getters['constant/providersByShortName'],
			store.getters['constant/genresByShortName']
		);

		// skip if no filter is specified in route
		if (!Object.keys(filterState).length) {
			return;
		}

		const webLocale = store.state.language.webLocale;
		const collectionType = store.getters['routing/activeCollectionType'];

		store.commit('filter/SET_FILTERS', {
			webLocale,
			collectionType,
			filterState: {
				...getDefaultFilterCollection(),
				...filterState,
			},
		});
	},

	getExperimentFromUrl(query: CollectionStateUrlQuery) {
		if (!query || !Object.keys(query).length) return;

		let add: ExperimentFromUrl | null = null;
		const setParams = query['preferred_variant'];
		if (setParams) {
			const [setExperiment, setVariant] = setParams ? setParams.split(':') : [];
			const sendSnowplowEvent = 'snowplow' in query ? true : false;
			add = {
				experiment: setExperiment,
				variant: setVariant,
				sendSnowplowEvent,
			};
		}

		let remove: ExperimentFromUrl | null = null;
		const disableParams = query['remove_preferred_experiment'];
		if (disableParams) {
			const [disableExperiment] = disableParams ? disableParams.split(':') : [];
			remove = {
				experiment: disableExperiment,
			};
		}

		return add || remove ? { add, remove } : undefined;
	},

	/**
	 * translates a filter state (FilterCollection) to a url.
	 */
	getUrlParamsFromFilterState(
		webLocale: WebLocale,
		routeName: string,
		filters: Partial<FilterCollection> = {},
		providersByShortName: Record<string, Provider | Package>,
		genresByShortName: Record<string, Genre>,
		collectionType: CollectionType,
		routeQuery: Route['query']
	) {
		const params: CollectionStateUrlParams = {};
		const query: CollectionStateUrlQuery = {};
		// add provider (singular) to params
		if (filters.providers && filters.providers.length === 1) {
			filters.providers
				.map(providerShortName => providersByShortName[providerShortName])
				.filter(Boolean)
				.forEach(provider => {
					// set the single provider param
					params.provider = provider.slug;
					if (['app.search.list', 'app.watchlist.list'].indexOf(routeName) !== -1) {
						if (filters.enable_provider_filter) {
							query.providers = 'short_name' in provider ? provider.short_name : provider.shortName;
						}
					}
				});
		}

		// handles state changes on offers (app.offers.list) pages
		// -> not sure if we really need this still?!

		// rename parameter keys for url matching defined in mappings
		const singularMap = {
			providers: { singular: 'provider', list: providersByShortName },
			genres: { singular: 'genre', list: genresByShortName },
		};

		const isGenrePage =
			collectionType == CollectionType.POPULAR &&
			(filters['genres'] || []).length === 1 &&
			(filters['genres'] || []).includes('eur');

		const keys = Object.keys(filters)
			.filter(key => !!(filters as any)[key])
			.filter(key => !ignoreFiltersOnUrl.includes(key)) as (keyof FilterCollection)[];
		keys.forEach((filterKey: keyof FilterCollection) => {
			switch (filterKey) {
				case 'providers':
				case 'genres': // genres are not yet singularly used
					const filterSingularKey = singularMap[filterKey].singular;
					const itemList = singularMap[filterKey].list;

					// add single provider or genre for single provider page or single genre page (which is not implemented yet)
					if ((filters[filterKey] || []).length === 1) {
						(filters[filterKey] || [])
							.map(shortName => itemList[shortName])
							.filter(Boolean)
							.forEach(item => {
								// set the single genre param
								params[filterSingularKey] = item.slug;
							});
					}

					// if filters is not singular, add it to query
					if (
						(filters[filterKey] || []).length !== 0 &&
						((filterKey === 'providers' && isGenrePage) ||
							(filterKey !== 'providers' && !isGenrePage) ||
							(filters[filterKey] || []).length > 1)
					) {
						if (collectionType === CollectionType.WATCHLIST && !filters.enable_provider_filter) {
							break;
						}
						query[filterKey] = getSortedQuery(filters, filterKey);
					}
					break;
				case 'age_certifications':
				case 'monetization_types':
				case 'presentation_types':
				case 'production_countries':
				case 'exclude_production_countries':
					if ((filters[filterKey] || []).length !== 0) {
						query[filterKey] = getSortedQuery(filters, filterKey);
					}
					break;
				case 'sorting_random_seed':
					if (filters.sort_by === TitleOrder.RANDOM) {
						query[filterKey] = (filters.sorting_random_seed || 0).toString();
					}
					break;
				case 'content_types':
					// apply content_type on search view & watchlist & leaving soon/coming soon (we don't have static urls for those)
					if (
						[
							'app.search.list',
							'app.watchlist.list',
							'app.titles.coming-soon.list',
							'app.titles.leaving-soon.list',
							'app.titles.coming-soon.provider',
							'app.titles.leaving-soon.provider',
						].indexOf(routeName) !== -1
					) {
						if ((filters[filterKey] || []).length !== 0) {
							query.content_type = getSortedQuery(filters, filterKey);
						}
					}
					if (isGenrePage && (filters[filterKey] || []).length !== 0) {
						query.content_type = getSortedQuery(filters, filterKey);
					}
					break;
				case 'scoring_filter_types':
					const scoringFilters = filters[filterKey];
					if (scoringFilters) {
						for (const ratingKey in scoringFilters) {
							const rating = scoringFilters[ratingKey];
							const scoring = (ratingMappings as any)[ratingKey];
							if (scoring) {
								(query as any)[scoring] = rating.min_scoring_value;
							}
						}
					}
					break;
				default:
					if (filters && (filters as any)[filterKey] !== null) {
						query[filterKey] = (filters as any)[filterKey];
					}
			}
		});

		// handle q and person_id query parameters
		// these parameters only exist on 'search' collectionTypes pages
		(['q', 'person_id'] as (keyof CollectionStateUrlQuery)[])
			.filter(key => routeQuery[key])
			.forEach(key => {
				if (collectionType === CollectionType.SEARCH) {
					query[key] = routeQuery[key] as string;
				}
			});

		return { params, query: <Dictionary<string>>query };
	},

	/**
	 * translates a url, split up in url params and url query, to a filter state (FilterCollection).
	 * usually this function will be called once upon startup to turn the url into filter state.
	 */
	getFilterStateFromUrl(
		routeName: RouteNames | undefined,
		params: CollectionStateUrlParams,
		query: CollectionStateUrlQuery,
		providersByShortName: Record<keyof Provider, Provider>[],
		genresByShortName: Record<keyof Genre, Genre>[]
	) {
		// PROVIDERS
		// const state = initialRouteState[routeName] || {};
		const state = routeName == null ? {} : { ...initialRouteState[routeName] };

		if (routeName === 'app.titles.popular.european') {
			state.genres = ['eur'];
			state.age_certifications = [];
			state.content_types = [];
			state.languages = null;
			state.min_price = null;
			state.max_price = null;
			state.monetization_types = [];
			state.presentation_types = [];
			state.providers = [];
			state.release_year_from = null;
			state.release_year_until = null;
			state.scoring_filter_types = null;
			state.timeline_type = null;
			// state.q = null;
			// state.person_id = null;
			state.sort_by = null;
			state.sort_asc = null;
		}

		if (params.provider) {
			(Object.keys(providersByShortName) as (keyof Provider)[])
				.map(shortName => (providersByShortName as any)[shortName])
				.filter((provider: Provider) => provider.slug === params.provider)
				.forEach((provider: Provider) => {
					state.providers = [provider.short_name];
				});
		} else if (query.providers) {
			state.providers = query.providers
				.split(',')
				.map(shortName => (providersByShortName as any)[shortName])
				.filter(Boolean)
				.map(provider => provider.short_name);
		}

		// GENRES
		if (params.genre) {
			(Object.keys(genresByShortName) as (keyof Genre)[])
				.map(shortName => (genresByShortName as any)[shortName])
				.filter((genre: Genre) => genre.slug === params.genre)
				.forEach((genre: Genre) => {
					state.genres = [genre.short_name];
				});
		}

		if (query.genres) {
			state.genres = query.genres
				.split(',')
				.map(shortName => (genresByShortName as any)[shortName])
				.filter(Boolean)
				.map(genre => genre.short_name);
		}
		// SINGLE VALUES
		[
			{ key: 'inner_tab', valueType: 'string' },
			{ key: 'min_price', valueType: 'number' },
			{ key: 'max_price', valueType: 'number' },
			{ key: 'release_year_from', valueType: 'number' },
			{ key: 'release_year_until', valueType: 'number' },
			{ key: 'timeline_type', valueType: 'string' },
			{ key: 'sort_by', valueType: 'string' },
			{ key: 'sort_asc', valueType: 'boolean' },
			{ key: 'enable_provider_filter', valueType: 'boolean' },
			{ key: 'event_date', valueType: 'string' },
		].forEach(item => {
			if (item.key in query) {
				const queryValue = query[item.key as keyof CollectionStateUrlQuery] || '';
				let value;

				switch (item.valueType) {
					case 'number':
						value = parseInt(queryValue);
						break;

					case 'boolean':
						value = queryValue === 'true';
						break;

					case 'string':
					default:
						value = queryValue;
						break;
				}

				(state as any)[item.key] = value;
			}
		});
		// CONCATENATED VALUES
		(
			['age_certifications', 'monetization_types', 'presentation_types'] as (keyof CollectionStateUrlQuery)[]
		).forEach(key => {
			if (key in query) {
				(state as any)[key] = (query as any)[key].split(',');
			}
		});
		// SPECIAL VALUES (scoring_filter_types)
		const scoringFilterTypes = SCORINGS.map(item => item.queryName)
			.filter(key => key in query)
			.reduce((root: any, key) => {
				const scoring = SCORINGS.find(scoring => scoring.queryName === key);
				const scoringKey = scoring ? scoring.provider : '';
				const scoringValue = parseFloat((query as any)[key]);
				root[scoringKey] = { min_scoring_value: scoringValue };
				return root;
			}, {});
		if (Object.keys(scoringFilterTypes).length) {
			(state as any)['scoring_filter_types'] =
				Object.keys(scoringFilterTypes).length > 0 ? scoringFilterTypes : null;
		}
		//drop sort_by filter as it is incorrect one(like for tomato_score)
		const sortBy = (state as any)['sort_by'];
		if (sortBy && !Object.values(TitleOrder).includes(sortBy as TitleOrder)) {
			(state as any)['sort_by'] = null;
			(state as any)['sort_asc'] = null;
		}

		// handle `q` and `person_id` parameters, remove them if they're not present
		// [{ name: 'q', value: query.q }, { name: 'person_id', value: parseInt(query.person_id || '', 10) }].forEach(
		// 	item => {
		// 		if ((query as any)[item.name]) {
		// 			(state as any)[item.name] = item.value;
		// 		} else {
		// 			delete (state as any)[item.name];
		// 		}
		// 	}
		// );

		if (query.content_type) {
			state.content_types = [query.content_type as TitleObjectType];
		}

		return state;
	},

	getSearchSuggesterStateFromUrl(query: CollectionStateUrlQuery) {
		return (['q', 'person_id'] as (keyof CollectionStateUrlQuery)[])
			.filter(key => query[key])
			.reduce((root, key) => {
				root[key] = query[key];
				return root;
			}, {} as CollectionStateUrlQuery);
	},
};

function getSortedQuery(filters: Partial<FilterCollection>, filterKey: keyof FilterCollection): string {
	return [...((filters[filterKey] || []) as any)].sort().join(',');
}

function getRouteName(
	webLocale: WebLocale,
	collectionType: CollectionType,
	filters: Partial<FilterCollection> = {}
): string {
	let routeName = '';

	switch (collectionType) {
		case CollectionType.SEARCH:
			routeName = 'app.search.list';
			break;
		case CollectionType.WATCHLIST:
			routeName = 'app.watchlist.list';
			break;
		case CollectionType.OFFERS:
			routeName = 'app.offers.list';
			break;
		case CollectionType.TITLELIST:
			routeName = 'app.landingpage.curatedlist';
			break;
		case CollectionType.EDITORIAL:
			routeName = 'app.editorial.article';
			break;
		case CollectionType.SPORTS:
			routeName = 'app.sports.all';
			break;
		case CollectionType.UPCOMING:
		case CollectionType.POPULAR:
			if (filters.genres?.length == 1 && filters.genres.includes('eur')) {
				routeName = 'app.titles.popular.european';
				break;
			}
			if (filters.content_types?.includes(TitleObjectType.DEALS_SUBSCRIPTION)) {
				routeName = 'app.titles.popular.provider-plans';
				break;
			}
		default:
			// Note: case CollectionType.POPULAR needs to be right before this with no break keyword
			const isLeavingSoonOrComingSoon = [CollectionType.COMING_SOON, CollectionType.LEAVING_SOON].includes(
				collectionType
			);

			const contentTypeFilter =
				(filters.content_types || [])[0] === TitleObjectType.MOVIE
					? TitleObjectType.MOVIE
					: TitleObjectType.SHOW;

			const hasOnlyOneProvider = (filters.providers || []).length === 1;
			const hasOnlyOneContentType = (filters.content_types || []).length === 1;
			if (hasOnlyOneProvider) {
				if (!hasOnlyOneContentType || isLeavingSoonOrComingSoon) {
					routeName = `app.titles.${collectionType}.provider`;
				} else {
					routeName = `app.titles.${collectionType}.provider-${contentTypeFilter}`;
				}
			} else {
				// Has 0 or more than 1 providers
				if (!hasOnlyOneContentType || isLeavingSoonOrComingSoon) {
					routeName = `app.titles.${collectionType}.list`;
				} else {
					routeName = `app.titles.${collectionType}.${contentTypeFilter}`;
				}
			}
	}

	return routeName;
}

export default StateHelper;
